import type { PipelineStep, PipelineStepType } from '../models/insightConfig.types'

import { cloneInsightValue, isInsightRecord } from './insightValue'

export const PROCESS_PIPELINE_STEP_TYPE = 'process' as const
export const PROCESS_TRANSFORM_TEMPLATES = {
  group_cost_breakdown: { group_by: [], sum_columns: [] },
} as const

export type InsightConfigServiceErrorCode =
  | 'insights.config.json.unknownField'
  | 'insights.config.json.invalid'
  | 'insights.config.json.nullForbidden'
  | 'insights.config.json.arrayRequired'
  | 'insights.config.json.objectRequired'
  | 'insights.config.pipeline.listRequired'
  | 'insights.config.pipeline.typeRequired'
  | 'insights.config.pipeline.invalidJson'
  | 'insights.config.pipeline.objectRequired'
  | 'insights.config.pipeline.transformsArrayRequired'
  | 'insights.config.pipeline.dynamicArrayRequired'

/** Machine-readable validation error; callers localize `code`. */
export class InsightConfigServiceError extends Error {
  constructor(
    readonly code: InsightConfigServiceErrorCode,
    readonly field?: string,
  ) {
    super(code)
    this.name = 'InsightConfigServiceError'
  }
}

export interface PipelineStepEditor {
  id: string
  type: string
  configText: string
}

export interface ProcessStepConfigState {
  isValid: boolean
  transformCount: number
  hasLegacyDynamicSteps: boolean
}

let pipelineEditorRowId = 0

function createDefaultPipelineStepConfig(stepType: string): Record<string, unknown> {
  return stepType === PROCESS_PIPELINE_STEP_TYPE ? { transform_steps: [] } : {}
}

export function normalizePipelineStepConfig(
  stepType: string | undefined,
  config: unknown,
): Record<string, unknown> {
  const nextConfig = isInsightRecord(config)
    ? cloneInsightValue(config)
    : createDefaultPipelineStepConfig(stepType ?? '')

  if (
    stepType === PROCESS_PIPELINE_STEP_TYPE &&
    !Array.isArray(nextConfig['transform_steps']) &&
    !Array.isArray(nextConfig['dynamic_steps'])
  ) {
    nextConfig['transform_steps'] = []
  }

  return nextConfig
}

export function normalizePipelineSteps(
  steps: unknown,
  fallbackSteps: unknown = [],
): PipelineStep[] {
  const source = Array.isArray(steps) ? steps : Array.isArray(fallbackSteps) ? fallbackSteps : []

  return source.map((candidate) => {
    const step = isInsightRecord(candidate) ? candidate : {}
    const type = typeof step['type'] === 'string' ? step['type'] : ''
    return {
      type: type as PipelineStepType,
      config: normalizePipelineStepConfig(type, step['config']),
    }
  })
}

export function buildPipelineStepEditors(
  steps: unknown = [],
  fallbackSteps: unknown = [],
): PipelineStepEditor[] {
  return normalizePipelineSteps(steps, fallbackSteps).map((step) => {
    pipelineEditorRowId += 1
    return {
      id: `pipeline-step-${pipelineEditorRowId}`,
      type: step.type,
      configText: JSON.stringify(normalizePipelineStepConfig(step.type, step.config), null, 2),
    }
  })
}

export function buildDefaultPipelineStepConfigText(stepType = ''): string {
  return JSON.stringify(createDefaultPipelineStepConfig(stepType), null, 2)
}

function parsePipelineStepConfigText(configText: unknown): unknown {
  const trimmed = typeof configText === 'string' ? configText.trim() : ''
  return trimmed ? JSON.parse(trimmed) : {}
}

function validateProcessStepConfig(config: Record<string, unknown>, field: string): void {
  if ('transform_steps' in config && !Array.isArray(config['transform_steps'])) {
    throw new InsightConfigServiceError('insights.config.pipeline.transformsArrayRequired', field)
  }
  if ('dynamic_steps' in config && !Array.isArray(config['dynamic_steps'])) {
    throw new InsightConfigServiceError('insights.config.pipeline.dynamicArrayRequired', field)
  }
}

export function buildPipelinePayload(stepEditors: unknown = []): PipelineStep[] {
  if (!Array.isArray(stepEditors)) {
    throw new InsightConfigServiceError('insights.config.pipeline.listRequired', 'pipeline_steps')
  }

  return stepEditors.map((candidate, index) => {
    const step = isInsightRecord(candidate) ? candidate : {}
    const field = `pipeline_steps[${index + 1}]`
    const type = typeof step['type'] === 'string' ? step['type'].trim() : ''
    if (!type) {
      throw new InsightConfigServiceError('insights.config.pipeline.typeRequired', field)
    }

    let parsedConfig: unknown
    try {
      parsedConfig = parsePipelineStepConfigText(step['configText'])
    } catch {
      throw new InsightConfigServiceError('insights.config.pipeline.invalidJson', field)
    }
    if (!isInsightRecord(parsedConfig)) {
      throw new InsightConfigServiceError('insights.config.pipeline.objectRequired', field)
    }
    if (type === PROCESS_PIPELINE_STEP_TYPE) validateProcessStepConfig(parsedConfig, field)

    return {
      type: type as PipelineStepType,
      config: normalizePipelineStepConfig(type, parsedConfig),
    }
  })
}

export function getProcessStepConfigState(configText = ''): ProcessStepConfigState {
  try {
    const parsedConfig = parsePipelineStepConfigText(configText)
    if (!isInsightRecord(parsedConfig)) {
      return { isValid: false, transformCount: 0, hasLegacyDynamicSteps: false }
    }
    const normalized = normalizePipelineStepConfig(PROCESS_PIPELINE_STEP_TYPE, parsedConfig)
    return {
      isValid: true,
      transformCount: Array.isArray(normalized['transform_steps'])
        ? normalized['transform_steps'].length
        : 0,
      hasLegacyDynamicSteps:
        Array.isArray(parsedConfig['dynamic_steps']) && parsedConfig['dynamic_steps'].length > 0,
    }
  } catch {
    return { isValid: false, transformCount: 0, hasLegacyDynamicSteps: false }
  }
}

export function appendProcessTransformTemplate(configText = '', transformType = ''): string {
  let parsedConfig: unknown
  try {
    parsedConfig = parsePipelineStepConfigText(configText)
  } catch {
    throw new InsightConfigServiceError('insights.config.pipeline.invalidJson', 'pipeline_steps')
  }
  if (!isInsightRecord(parsedConfig)) {
    throw new InsightConfigServiceError('insights.config.pipeline.objectRequired', 'pipeline_steps')
  }

  const normalized = normalizePipelineStepConfig(PROCESS_PIPELINE_STEP_TYPE, parsedConfig)
  const template =
    PROCESS_TRANSFORM_TEMPLATES[transformType as keyof typeof PROCESS_TRANSFORM_TEMPLATES] ?? {}
  normalized['transform_steps'] = [
    ...(Array.isArray(normalized['transform_steps']) ? normalized['transform_steps'] : []),
    { type: transformType, config: cloneInsightValue(template) },
  ]
  return JSON.stringify(normalized, null, 2)
}

export function hasLlmPipelineStep(steps: unknown = []): boolean {
  return (
    Array.isArray(steps) &&
    steps.some((candidate) => {
      if (!isInsightRecord(candidate)) return false
      return candidate['type'] === 'llm_enrich' || candidate['type'] === 'llm_postprocess'
    })
  )
}
